import { getFromDataPath, mutateByPath, removeByPath } from "../../../utility";
import { ArsTableCellType, ArsTableState, ArstActionContextmenuAction } from "../interface";
import { isArsTableGroupCalculationDto } from "../typeguards";
import { arst_resolveTargetPath } from "./arst_resolveTargetPath";

/**
 * Row-axis cell types that can carry space-after settings.
 */
const rowAfterCellTypes = new Set<ArsTableCellType>([
  ArsTableCellType.RowGroupCell,
  ArsTableCellType.RowGroupLabel,
  ArsTableCellType.RowOperation,
]);

/**
 * Toggles a space-after setting on the row item targeted by a context-menu action.
 * Group and data value labels resolve to their parent grouping factor. The setting is
 * removed when switched off so that only enabled settings are sent to the back end.
 *
 * @param state - Current ARS table state
 * @param action - Context-menu action identifying the selected row cell
 * @param property - Space-after setting to toggle: `spaceAfterGroup` for the whole item, or
 *        `spaceAfterSubGroups` for each group or data value of a grouping factor
 * @returns Updated ARS table state, or the original state when the cell cannot carry the setting
 */
export const arst_toggleSpaceAfter = <T>(
  state: ArsTableState<T>,
  action: ArstActionContextmenuAction<T>,
  property: "spaceAfterGroup" | "spaceAfterSubGroups",
): ArsTableState<T> => {
  if (rowAfterCellTypes.has(action.cellValue.cellType)) {
    const targetPath = arst_resolveTargetPath(action);
    const targetItem = getFromDataPath(targetPath, state.bodyData, (obj) =>
      isArsTableGroupCalculationDto(obj, state.settings.editorProps.isT),
    );
    if (targetItem && (property === "spaceAfterGroup" || "groupingFactor" in targetItem)) {
      // Only grouping factors carry spaceAfterSubGroups, so other items can only be toggling spaceAfterGroup
      const isEnabled =
        "groupingFactor" in targetItem ? targetItem[property] : targetItem.spaceAfterGroup;
      return {
        ...state,
        bodyData: isEnabled
          ? removeByPath(state.bodyData, `${targetPath}/${property}`)
          : mutateByPath(state.bodyData, `${targetPath}/${property}`, true),
        changed: true,
      };
    }
  }
  return state;
};
